/**
 * Shared optimistic cache updates for kanban board moves.
 *
 * Every board persists a drop as `{stage, beforeItemId, afterItemId}`; these
 * helpers mirror that exact placement into the React Query cache so the board
 * shows the dropped order immediately and the post-settle refetch is a visual
 * no-op. They write via `getQueriesData` over a PREFIX key — boards key their
 * list queries with filter objects (search, priorities, …), and an exact-key
 * `setQueryData` silently misses every filtered variant.
 *
 * @module features/boards/optimisticMove
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';

export type MoveTarget = {
  /** Item id the moved card sits in front of, or null. */
  beforeItemId: string | null;
  /** Item id the moved card sits behind, or null. */
  afterItemId: string | null;
};

/** Rollback snapshot: the cache entries exactly as they were pre-write. */
export type MoveSnapshot = Array<[QueryKey, unknown]>;

/**
 * Pure splice: removes `item` (if present), then inserts it before
 * `beforeItemId` / after `afterItemId`. When neither neighbor is found, falls
 * back to "after the last item satisfying `isSameColumn`" (empty-column /
 * append placement in a flat multi-column list), else the end of the list.
 */
export function spliceIntoList<TItem>(
  list: readonly TItem[],
  item: TItem,
  getId: (item: TItem) => string,
  target: MoveTarget,
  isSameColumn?: (item: TItem) => boolean
): TItem[] {
  const itemId = getId(item);
  const without = list.filter((it) => getId(it) !== itemId);

  let insertAt = -1;
  if (target.beforeItemId) {
    insertAt = without.findIndex((it) => getId(it) === target.beforeItemId);
  }
  if (insertAt < 0 && target.afterItemId) {
    const afterIdx = without.findIndex((it) => getId(it) === target.afterItemId);
    if (afterIdx >= 0) insertAt = afterIdx + 1;
  }
  if (insertAt < 0 && isSameColumn) {
    const lastInColumn = without.reduce((acc, it, idx) => (isSameColumn(it) ? idx : acc), -1);
    if (lastInColumn >= 0) insertAt = lastInColumn + 1;
  }
  if (insertAt < 0) insertAt = without.length;

  return [...without.slice(0, insertAt), item, ...without.slice(insertAt)];
}

/**
 * Optimistic move for boards whose cache shape is a FLAT item array per query
 * (product items, cards): every cached variant under `prefixKey` that
 * contains the item gets the moved item re-spliced at the dropped position.
 * Variants that don't contain the item (filtered out) are left untouched.
 * Returns the rollback snapshot for `onError`.
 */
export function moveInFlatLists<TItem>(
  qc: QueryClient,
  prefixKey: QueryKey,
  opts: {
    itemId: string;
    /** Applies the move's field changes (e.g. set `stage`). */
    updateItem: (item: TItem) => TItem;
    target: MoveTarget;
    getId: (item: TItem) => string;
    /** Membership in the DESTINATION column (for empty-column append). */
    isSameColumn: (item: TItem) => boolean;
  }
): MoveSnapshot {
  const snapshot: MoveSnapshot = [];
  for (const [key, data] of qc.getQueriesData<TItem[]>({ queryKey: prefixKey })) {
    if (!Array.isArray(data)) continue;
    const existing = data.find((it) => opts.getId(it) === opts.itemId);
    if (!existing) continue;
    snapshot.push([key, data]);
    const moved = opts.updateItem(existing);
    qc.setQueryData<TItem[]>(
      key,
      spliceIntoList(data, moved, opts.getId, opts.target, opts.isSameColumn)
    );
  }
  return snapshot;
}

/**
 * Optimistic move for boards whose cache is ONE QUERY PER COLUMN (tasks):
 * source-column variants drop the item, target-column variants splice it in
 * at the dropped position. `readList`/`writeList` adapt the response envelope
 * (e.g. `{data, pagination}`); `countDelta` lets the adapter keep header
 * counts consistent.
 */
export function moveAcrossColumnLists<TResp, TItem>(
  qc: QueryClient,
  opts: {
    prefixKey: QueryKey;
    /** Resolve a cached query key to its column id, or null to skip it. */
    matchColumn: (key: QueryKey) => string | null;
    fromColumnId: string;
    toColumnId: string;
    item: TItem;
    updateItem: (item: TItem) => TItem;
    target: MoveTarget;
    getId: (item: TItem) => string;
    /** Extract the item list from the response, or null to skip (shape mismatch). */
    readList: (resp: TResp) => TItem[] | null;
    writeList: (resp: TResp, list: TItem[], countDelta: number) => TResp;
  }
): MoveSnapshot {
  const snapshot: MoveSnapshot = [];
  const itemId = opts.getId(opts.item);

  for (const [key, data] of qc.getQueriesData<TResp>({ queryKey: opts.prefixKey })) {
    if (data === undefined) continue;
    const column = opts.matchColumn(key);
    if (column === null) continue;
    const list = opts.readList(data);
    if (list === null) continue;

    const has = list.some((it) => opts.getId(it) === itemId);

    if (column === opts.toColumnId) {
      // Same-column reorders land here too (from === to): re-splice in place.
      const moved = opts.updateItem(opts.item);
      const next = spliceIntoList(list, moved, opts.getId, opts.target);
      snapshot.push([key, data]);
      qc.setQueryData<TResp>(key, opts.writeList(data, next, has ? 0 : 1));
    } else if (column === opts.fromColumnId && has) {
      const next = list.filter((it) => opts.getId(it) !== itemId);
      snapshot.push([key, data]);
      qc.setQueryData<TResp>(key, opts.writeList(data, next, -1));
    }
  }
  return snapshot;
}

/** Restores the cache entries captured by a move helper. */
export function rollbackMove(qc: QueryClient, snapshot: MoveSnapshot): void {
  for (const [key, data] of snapshot) {
    qc.setQueryData(key, data);
  }
}
